// ABOUTME: The safety-net poll interval the realtime provider derives from its connection, as a
// ABOUTME: context: rare while the channel delivers events, today's cadence while it does not; and the connection itself.
import { createContext, useContext, useEffect, useState } from "react"

/** Connected: the channel carries the changes, the poll only covers what it might have missed. */
export const POLL_INTERVAL_CONNECTED_MS = 15 * 60 * 1000
/** Disconnected (Centrifugo down, a path that cuts websockets): the cadence the calendar always had. */
export const POLL_INTERVAL_DISCONNECTED_MS = 5 * 60 * 1000

/**
 * The interval rather than the connection flag: a flapping link must re-render
 * its readers only when the interval itself moves — at most once per interval
 * (usePollIntervalForConnection). Outside the provider it reads as disconnected.
 */
export const RealtimePollIntervalContext = createContext<number>(POLL_INTERVAL_DISCONNECTED_MS)

/**
 * Whether the channel delivers events right now. No hysteresis, unlike the interval: what polls only
 * while there are no events — an open discussion feed — must start the moment they stop. Outside the
 * provider it reads as not live.
 */
export const RealtimeLiveContext = createContext(false)

export const useRealtimeLive = (): boolean => useContext(RealtimeLiveContext)

/**
 * ONLY for a refresher that is itself the realtime refresh path — today the
 * calendar window's tick, the coach screen's chunk fetchers, the race
 * countdown's read (startsRefresh), and an open discussion feed's safety poll
 * while the channel is live.
 *
 * The rule, which is self-checking: a query polls only when nothing else
 * refreshes it, so anything still polling must poll at the disconnected
 * cadence. Stretching a poller that no event reaches to 15 minutes takes away
 * two thirds of its freshness and gives nothing back.
 */
export const useRealtimeRefreshInterval = (): number => useContext(RealtimePollIntervalContext)

/**
 * The poll interval a channel state implies; the realtime provider computes it
 * once and publishes it through the context above. Losing the channel tightens
 * the interval at once — nothing else delivers the changes now. Loosening
 * waits until the channel has been up, without a break, for one whole
 * connected interval: every state change restarts that wait, so a flapping
 * channel never earns the rare cadence, and the poll timer is not restarted on
 * every flap.
 */
/**
 * How long the channel must hold before the rare cadence is earned. Its own
 * value, not the connected interval: waiting a whole 15 minutes to relax meant
 * the first refresh after the channel came up could be half an hour away, while
 * one flap still costs the wait — which is all this is for.
 */
export const CONNECTION_STABLE_MS = POLL_INTERVAL_DISCONNECTED_MS

export function usePollIntervalForConnection(isConnected: boolean): number {
  // Whether the channel has held for the stability window since it last came up.
  const [hasHeld, setHasHeld] = useState(false)
  const [prevConnected, setPrevConnected] = useState(isConnected)
  // Every state change restarts the wait, in the render that sees it: a loss
  // tightens at once, and uptime before it earns nothing.
  if (isConnected !== prevConnected) {
    setPrevConnected(isConnected)
    setHasHeld(false)
  }

  useEffect(() => {
    if (!isConnected) return
    const timer = setTimeout(() => setHasHeld(true), CONNECTION_STABLE_MS)
    return () => clearTimeout(timer)
  }, [isConnected])

  // No `isConnected &&` needed: the timer runs only while connected, and a loss
  // clears `hasHeld` in the very render that sees it.
  return hasHeld ? POLL_INTERVAL_CONNECTED_MS : POLL_INTERVAL_DISCONNECTED_MS
}
